import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { useExcelImport } from '@/hooks/useExcelImport';
import { useSites } from '@/hooks/useSites';
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';
import { QuickRoomAssignPanel } from '@/components/clients/QuickRoomAssignPanel';

export type ExcelImportType = 'roster' | 'documents' | 'intake';

interface ExcelImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: ExcelImportType;
}

export function ExcelImportDialog({ open, onOpenChange, type }: ExcelImportDialogProps) {
  const { sites } = useSites();
  const { isImporting, importRoster, importDocuments, importNewClientIntake, downloadTemplate } = useExcelImport();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [importResult, setImportResult] = useState<any>(null);
  const [showAssignPanel, setShowAssignPanel] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        alert('Please upload a valid Excel file (.xlsx or .xls)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !selectedSite) {
      alert('Please select a file and facility');
      return;
    }
    let result;
    if (type === 'roster') {
      result = await importRoster(selectedFile, selectedSite);
    } else if (type === 'intake') {
      result = await importNewClientIntake(selectedFile, selectedSite);
    } else {
      result = await importDocuments(selectedFile, selectedSite);
    }
    setImportResult(result);
  };

  const handleReset = () => {
    setSelectedFile(null);
    setSelectedSite('');
    setImportResult(null);
    setShowAssignPanel(false);
  };

  const handleClose = () => {
    handleReset();
    onOpenChange(false);
  };

  const title =
    type === 'roster' ? 'Import Roster from Excel'
    : type === 'intake' ? 'Import New Clients from Excel'
    : 'Import Documents from Excel';

  const description =
    type === 'intake'
      ? 'Bulk-create new client records. Clients land as unbedded; a Manager or Supervisor assigns rooms next.'
      : `Upload an Excel file to bulk import ${type === 'roster' ? 'client roster data' : 'documents'}. Download the template for the correct format.`;

  const createdIds: string[] = importResult?.createdClientIds || [];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{showAssignPanel ? 'Assign Rooms' : title}</DialogTitle>
          <DialogDescription>
            {showAssignPanel ? 'Pick a room for each newly imported client.' : description}
          </DialogDescription>
        </DialogHeader>

        {showAssignPanel && selectedSite ? (
          <QuickRoomAssignPanel
            clientIds={createdIds.length > 0 ? createdIds : undefined}
            siteId={selectedSite}
            onDone={handleClose}
          />
        ) : (
        <div className="space-y-6">
          {/* Template Download */}
          <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Download Template</p>
                <p className="text-sm text-muted-foreground">
                  Use this template to ensure proper formatting
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => downloadTemplate(type)}>
              <Download className="h-4 w-4 mr-2" />
              Template
            </Button>
          </div>

          {/* Facility Selection */}
          <div>
            <Label>Select Facility *</Label>
            <Select value={selectedSite} onValueChange={setSelectedSite}>
              <SelectTrigger>
                <SelectValue placeholder="Choose facility" />
              </SelectTrigger>
              <SelectContent>
                {sites.map(site => (
                  <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* File Upload */}
          <div>
            <Label>Upload Excel File *</Label>
            <div className="mt-2">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                id="excel-upload"
                disabled={isImporting}
              />
              <label
                htmlFor="excel-upload"
                className="flex items-center justify-center w-full p-6 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <div className="text-center">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm font-medium">
                    {selectedFile ? selectedFile.name : 'Click to upload Excel file'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">.xlsx or .xls (max 5MB)</p>
                </div>
              </label>
            </div>
          </div>

          {isImporting && (
            <div className="space-y-2">
              <Label>Importing...</Label>
              <Progress value={undefined} className="w-full" />
            </div>
          )}

          {importResult && (
            <div className="space-y-3">
              <Alert variant={importResult.failed > 0 && importResult.success === 0 ? 'destructive' : 'default'}>
                <div className="flex items-start gap-2">
                  {importResult.failed > 0 && importResult.success === 0 ? (
                    <AlertCircle className="h-5 w-5" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  )}
                  <div className="flex-1">
                    <AlertDescription>
                      <div className="font-medium mb-2">Import Complete</div>
                      <div className="space-y-1 text-sm">
                        <div>✓ Imported: {importResult.success}</div>
                        {importResult.failed > 0 && <div>✗ Skipped: {importResult.failed}</div>}
                        {type === 'intake' && importResult.success > 0 && (
                          <div className="mt-1 text-muted-foreground">
                            {importResult.success} client{importResult.success === 1 ? '' : 's'} awaiting room assignment.
                          </div>
                        )}
                      </div>
                    </AlertDescription>
                  </div>
                </div>
              </Alert>

              {importResult.errors?.length > 0 && (
                <div className="max-h-40 overflow-y-auto p-3 bg-muted rounded-lg">
                  <p className="font-medium text-sm mb-2">Notes:</p>
                  <ul className="space-y-1 text-xs">
                    {importResult.errors.map((error: string, idx: number) => (
                      <li key={idx} className="text-muted-foreground">• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {type === 'intake' && createdIds.length > 0 && (
                <Button className="w-full" onClick={() => setShowAssignPanel(true)}>
                  Assign Rooms Now
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          )}

          {/* Required Fields Info */}
          <Alert>
            <AlertDescription>
              <p className="font-medium mb-2">Required Columns:</p>
              <ul className="text-sm space-y-1">
                {type === 'roster' && (
                  <>
                    <li>• first_name, last_name</li>
                    <li>• room_number (optional)</li>
                    <li>• date_of_birth, gender, phone, email (all optional)</li>
                  </>
                )}
                {type === 'intake' && (
                  <>
                    <li>• <strong>first_name</strong> — required</li>
                    <li>• <strong>last_name</strong> — required</li>
                    <li>• date_of_birth (optional, any common date format)</li>
                    <li>• phone (optional)</li>
                  </>
                )}
                {type === 'documents' && (
                  <>
                    <li>• title, content</li>
                    <li>• client_first_name, client_last_name (optional)</li>
                  </>
                )}
              </ul>
            </AlertDescription>
          </Alert>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleImport}
              disabled={!selectedFile || !selectedSite || isImporting}
              className="flex-1"
            >
              {isImporting ? 'Importing...' : 'Import Data'}
            </Button>
            {importResult && (
              <Button variant="outline" onClick={handleReset}>Import Another</Button>
            )}
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
